import 'regenerator-runtime/runtime';
import React, { useRef, useEffect, useState } from 'react';
import { GameEngine } from 'react-game-engine';
import { Button } from '../ui/button';
import { ArrowLeft, ArrowRight, Space, RotateCcw, Volume2, VolumeX } from 'lucide-react';

// Game constants
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const GRAVITY = 0.8;
const PLAYER_SPEED = 5;
const JUMP_FORCE = -15;
const GROUND_HEIGHT = 100;

// Simple sound manager without WebAudio to avoid errors
class SoundManager {
  private enabled = true;
  private lastPlayTime: { [key: string]: number } = {};

  constructor() {
    // No WebAudio setup to avoid errors
  }

  play(soundName: string) {
    if (!this.enabled) return;
    
    // Prevent rapid-fire sounds (debounce)
    const now = Date.now();
    if (this.lastPlayTime[soundName] && now - this.lastPlayTime[soundName] < 200) {
      return;
    }
    this.lastPlayTime[soundName] = now;

    // Simple beep using oscillator for immediate feedback
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for different sounds
      const frequencies: { [key: string]: number } = {
        jump: 220,
        collect: 440,
        death: 150,
        hit: 100
      };
      
      oscillator.frequency.setValueAtTime(frequencies[soundName] || 200, audioContext.currentTime);
      oscillator.type = soundName === 'collect' ? 'square' : 'sine';
      
      // Quick fade out
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
      
      // Clean up
      setTimeout(() => audioContext.close(), 200);
    } catch (error) {
      // Silent fail if audio doesn't work
    }
  }

  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  isEnabled() {
    return this.enabled;
  }
}

// Game entities
interface Entity {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

interface Player extends Entity {
  velocityX: number;
  velocityY: number;
  onGround: boolean;
  lives: number;
}

interface Platform extends Entity {
  type: 'ground' | 'platform' | 'moving';
  moveDirection?: number;
  moveSpeed?: number;
  moveRange?: number;
  originalX?: number;
}

interface Collectible extends Entity {
  collected: boolean;
  type: 'coin' | 'powerup';
}

interface Enemy extends Entity {
  velocityX: number;
  direction: number;
}

interface GameState {
  player: Player;
  platforms: Platform[];
  collectibles: Collectible[];
  enemies: Enemy[];
  camera: { x: number; y: number };
  score: number;
  gameStarted: boolean;
  gameOver: boolean;
  level: number;
  keysPressed: { [key: string]: boolean };
  worldLength: number;
}

// Procedural level generation
const generateLevel = (level: number): { platforms: Platform[], collectibles: Collectible[], enemies: Enemy[] } => {
  const platforms: Platform[] = [];
  const collectibles: Collectible[] = [];
  const enemies: Enemy[] = [];
  
  const worldLength = 2000 + level * 500;
  
  // No ground platforms - player must stay on floating platforms!
  
  // Add starting platform
  platforms.push({
    id: 'start-platform',
    x: 0,
    y: GAME_HEIGHT - 200,
    width: 150,
    height: 20,
    color: '#4a5568',
    type: 'platform'
  });
  
  // Generate platforms, collectibles, and enemies
  for (let x = 200; x < worldLength - 200; x += 150 + Math.random() * 200) {
    const platformY = GAME_HEIGHT - GROUND_HEIGHT - 80 - Math.random() * 200;
    const platformWidth = 80 + Math.random() * 120;
    
    // Platform
    const isMoving = Math.random() < 0.3;
    platforms.push({
      id: `platform-${x}`,
      x,
      y: platformY,
      width: platformWidth,
      height: 20,
      color: '#2d3748',
      type: isMoving ? 'moving' : 'platform',
      moveDirection: isMoving ? (Math.random() < 0.5 ? 1 : -1) : undefined,
      moveSpeed: isMoving ? 1 + Math.random() : undefined,
      moveRange: isMoving ? 50 + Math.random() * 100 : undefined,
      originalX: isMoving ? x : undefined
    });
    
    // Collectibles on platforms
    if (Math.random() < 0.7) {
      collectibles.push({
        id: `coin-${x}`,
        x: x + platformWidth / 2 - 10,
        y: platformY - 25,
        width: 20,
        height: 20,
        color: '#ffd700',
        collected: false,
        type: 'coin'
      });
    }
    
    // Enemies on platforms
    if (Math.random() < 0.4 && level > 1) {
      enemies.push({
        id: `enemy-${x}`,
        x: x + 10,
        y: platformY - 30,
        width: 25,
        height: 30,
        color: '#e53e3e',
        velocityX: (Math.random() < 0.5 ? -1 : 1) * (1 + Math.random()),
        direction: Math.random() < 0.5 ? -1 : 1
      });
    }
  }
  
  return { platforms, collectibles, enemies };
};

// Game systems
const MovementSystem = (entities: any, { touches, screen }: any) => {
  const player = entities.player;
  
  // Initialize keysPressed if not exists
  if (!entities.keysPressed) {
    entities.keysPressed = {};
  }
  
  // Handle input
  let moveLeft = entities.keysPressed['ArrowLeft'] || entities.keysPressed['a'] || entities.keysPressed['A'];
  let moveRight = entities.keysPressed['ArrowRight'] || entities.keysPressed['d'] || entities.keysPressed['D'];
  let jump = entities.keysPressed['ArrowUp'] || entities.keysPressed['w'] || entities.keysPressed['W'] || entities.keysPressed[' '];
  
  // Touch controls
  if (touches && touches.length > 0) {
    const touch = touches[0];
    const touchX = touch.event?.pageX || touch.x || 0;
    const touchY = touch.event?.pageY || touch.y || 0;
    
    if (touch.type === 'start' || touch.type === 'move') {
      if (touchX < screen.width / 3) {
        moveLeft = true;
      } else if (touchX > (2 * screen.width) / 3) {
        moveRight = true;
      } else {
        jump = true;
      }
    }
  }
  
  // Horizontal movement
  if (moveLeft) {
    player.velocityX = Math.max(player.velocityX - 1, -PLAYER_SPEED);
  } else if (moveRight) {
    player.velocityX = Math.min(player.velocityX + 1, PLAYER_SPEED);
  } else {
    player.velocityX *= 0.8; // Friction
  }
  
  // Jumping
  if (jump && player.onGround) {
    player.velocityY = JUMP_FORCE;
    player.onGround = false;
    entities.soundManager?.play('jump');
  }
  
  // Apply gravity
  player.velocityY += GRAVITY;
  
  // Update position
  player.x += player.velocityX;
  player.y += player.velocityY;
  
  return entities;
};

const CollisionSystem = (entities: any) => {
  const player = entities.player;
  const platforms = entities.platforms;
  const collectibles = entities.collectibles;
  const enemies = entities.enemies;
  
  player.onGround = false;
  
  // Platform collisions
  platforms.forEach((platform: Platform) => {
    if (
      player.x < platform.x + platform.width &&
      player.x + player.width > platform.x &&
      player.y + player.height > platform.y &&
      player.y + player.height < platform.y + platform.height + 20
    ) {
      if (player.velocityY > 0) {
        player.y = platform.y - player.height;
        player.velocityY = 0;
        player.onGround = true;
      }
    }
  });
  
  // Collectible collisions
  collectibles.forEach((collectible: Collectible) => {
    if (
      !collectible.collected &&
      player.x < collectible.x + collectible.width &&
      player.x + player.width > collectible.x &&
      player.y < collectible.y + collectible.height &&
      player.y + player.height > collectible.y
    ) {
      collectible.collected = true;
      entities.score += collectible.type === 'coin' ? 100 : 500;
      entities.soundManager?.play('collect');
    }
  });
  
  // Enemy collisions
  enemies.forEach((enemy: Enemy) => {
    if (
      player.x < enemy.x + enemy.width &&
      player.x + player.width > enemy.x &&
      player.y < enemy.y + enemy.height &&
      player.y + player.height > enemy.y
    ) {
      // Player hit enemy
      if (player.velocityY > 0 && player.y < enemy.y) {
        // Jump on enemy
        enemy.x = -1000; // Remove enemy
        player.velocityY = JUMP_FORCE / 2;
        entities.score += 200;
        entities.soundManager?.play('collect');
      } else {
        // Player takes damage
        if (!entities.playerHit) {
          entities.playerHit = true;
          player.lives--;
          player.x -= 50; // Knockback
          entities.soundManager?.play('hit');
          if (player.lives <= 0) {
            entities.gameOver = true;
            entities.gameStarted = false;
            entities.soundManager?.play('death');
          }
          // Reset hit flag after a short delay
          setTimeout(() => {
            entities.playerHit = false;
          }, 1000);
        }
      }
    }
  });
  
  // Boundary checks - fall death!
  if (player.x < 0) player.x = 0;
  if (player.y > GAME_HEIGHT && !entities.deathTriggered) {
    // Player fell off the screen - immediate death!
    console.log("Player died! Y position:", player.y, "Game height:", GAME_HEIGHT);
    entities.deathTriggered = true;
    entities.gameOver = true;
    entities.gameStarted = false;
    entities.soundManager?.play('death');
    
    // Force immediate state change by calling external function
    setTimeout(() => {
      const globalWindow = window as any;
      if (globalWindow.platformerGameDied) {
        globalWindow.platformerGameDied(entities.score, entities.level);
      }
    }, 50);
  }
  
  return entities;
};

const EnemySystem = (entities: any) => {
  const enemies = entities.enemies;
  const platforms = entities.platforms;
  
  enemies.forEach((enemy: Enemy) => {
    // Move enemy
    enemy.x += enemy.velocityX;
    
    // Simple AI: turn around at platform edges
    let onPlatform = false;
    platforms.forEach((platform: Platform) => {
      if (
        enemy.y + enemy.height >= platform.y &&
        enemy.y + enemy.height <= platform.y + platform.height + 10 &&
        enemy.x + enemy.width > platform.x &&
        enemy.x < platform.x + platform.width
      ) {
        onPlatform = true;
      }
    });
    
    // Turn around if at edge or hit wall
    if (!onPlatform || enemy.x <= 0) {
      enemy.velocityX *= -1;
    }
  });
  
  return entities;
};

const MovingPlatformSystem = (entities: any) => {
  const platforms = entities.platforms;
  
  platforms.forEach((platform: Platform) => {
    if (platform.type === 'moving' && platform.moveDirection && platform.moveSpeed && platform.moveRange && platform.originalX !== undefined) {
      platform.x += platform.moveDirection * platform.moveSpeed;
      
      if (Math.abs(platform.x - platform.originalX) > platform.moveRange) {
        platform.moveDirection *= -1;
      }
    }
  });
  
  return entities;
};

const CameraSystem = (entities: any) => {
  const player = entities.player;
  const camera = entities.camera;
  
  // Follow player with some offset
  const targetX = player.x - GAME_WIDTH / 3;
  camera.x = camera.x + (targetX - camera.x) * 0.1;
  
  // Keep camera within world bounds
  if (camera.x < 0) camera.x = 0;
  if (camera.x > entities.worldLength - GAME_WIDTH) {
    camera.x = entities.worldLength - GAME_WIDTH;
  }
  
  // Check level completion
  if (player.x > entities.worldLength - 100) {
    entities.level++;
    const newLevel = generateLevel(entities.level);
    entities.platforms = newLevel.platforms;
    entities.collectibles = newLevel.collectibles;
    entities.enemies = newLevel.enemies;
    entities.worldLength = 2000 + entities.level * 500;
    player.x = 75;
    player.y = GAME_HEIGHT - 250;
    camera.x = 0;
  }
  
  return entities;
};

// Renderer
const GameRenderer = (entities: any, screen: any) => {
  // Add null checks to prevent crashes
  if (!entities || !entities.camera || !entities.player) {
    return <div style={{ width: GAME_WIDTH, height: GAME_HEIGHT, background: '#87ceeb' }} />;
  }
  
  const camera = entities.camera;
  
  return (
    <div 
      style={{ 
        width: GAME_WIDTH, 
        height: GAME_HEIGHT, 
        position: 'relative', 
        overflow: 'hidden',
        background: 'linear-gradient(to bottom, #87ceeb 0%, #98d8e8 50%, #b0e2ff 100%)'
      }}
    >
      {/* Platforms */}
      {entities.platforms.map((platform: Platform) => (
        <div
          key={platform.id}
          style={{
            position: 'absolute',
            left: platform.x - camera.x,
            top: platform.y,
            width: platform.width,
            height: platform.height,
            backgroundColor: platform.color,
            border: '2px solid #1a202c'
          }}
        />
      ))}
      
      {/* Collectibles */}
      {entities.collectibles.map((collectible: Collectible) => 
        !collectible.collected && (
          <div
            key={collectible.id}
            style={{
              position: 'absolute',
              left: collectible.x - camera.x,
              top: collectible.y,
              width: collectible.width,
              height: collectible.height,
              backgroundColor: collectible.color,
              borderRadius: '50%',
              border: '2px solid #b7791f',
              boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}
          />
        )
      )}
      
      {/* Enemies */}
      {entities.enemies.map((enemy: Enemy) => 
        enemy.x > -100 && (
          <div
            key={enemy.id}
            style={{
              position: 'absolute',
              left: enemy.x - camera.x,
              top: enemy.y,
              width: enemy.width,
              height: enemy.height,
              backgroundColor: enemy.color,
              border: '2px solid #9b2c2c',
              borderRadius: '4px'
            }}
          />
        )
      )}
      
      {/* Player */}
      <div
        style={{
          position: 'absolute',
          left: entities.player.x - camera.x,
          top: entities.player.y,
          width: entities.player.width,
          height: entities.player.height,
          backgroundColor: entities.player.color,
          border: '2px solid #2b6cb0',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}
      />
      
      {/* UI */}
      <div style={{ position: 'absolute', top: 10, left: 10, color: 'white', fontFamily: 'monospace', fontSize: '16px', textShadow: '1px 1px 2px black' }}>
        <div>Score: {entities.score}</div>
        <div>Level: {entities.level}</div>
        <div>Lives: {entities.player.lives}</div>
      </div>
    </div>
  );
};

interface PlatformerGameProps {
  onClose: () => void;
}

export const PlatformerGame: React.FC<PlatformerGameProps> = ({ onClose }) => {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'gameOver'>('menu');
  const [gameKey, setGameKey] = useState(0);
  const soundManagerRef = useRef<SoundManager>(new SoundManager());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const keysRef = useRef<{ [key: string]: boolean }>({});
  const gameStateRef = useRef<any>(null);
  const [finalScore, setFinalScore] = useState(0);
  const [finalLevel, setFinalLevel] = useState(1);

  const resetGame = () => {
    // Clear all keys and reset state
    keysRef.current = {};
    setGameKey(prev => prev + 1);
    setGameState('menu');
    setFinalScore(0);
    setFinalLevel(1);
  };

  const toggleSound = () => {
    const enabled = soundManagerRef.current.toggle();
    setSoundEnabled(enabled);
  };

  const initialLevel = generateLevel(1);
  
  const initialEntities = {
    player: {
      id: 'player',
      x: 75,
      y: GAME_HEIGHT - 250,
      width: 30,
      height: 40,
      color: '#3182ce',
      velocityX: 0,
      velocityY: 0,
      onGround: false,
      lives: 3
    },
    platforms: initialLevel.platforms,
    collectibles: initialLevel.collectibles,
    enemies: initialLevel.enemies,
    camera: { x: 0, y: 0 },
    score: 0,
    gameStarted: false,
    gameOver: false,
    level: 1,
    keysPressed: keysRef.current,
    worldLength: 2000 + 500,
    soundManager: soundManagerRef.current,
    deathTriggered: false,  // Reset death flag
    playerHit: false
  };

  // Set up death callback - simplified without protection
  useEffect(() => {
    (window as any).platformerGameDied = (score: number, level: number) => {
      console.log("Death callback triggered with score:", score, "level:", level);
      setFinalScore(score);
      setFinalLevel(level);
      setGameState('gameOver');
    };
    
    return () => {
      delete (window as any).platformerGameDied;
    };
  }, [gameKey]); // Reset when game resets

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameState !== 'playing') return;
      keysRef.current[e.key] = true;
      e.preventDefault();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (gameState !== 'playing') return;
      keysRef.current[e.key] = false;
      e.preventDefault();
    };

    // Clear all keys when game state changes
    if (gameState !== 'playing') {
      keysRef.current = {};
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState]);

  // Game Over Screen
  if (gameState === 'gameOver') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-red-400 to-red-600 flex flex-col items-center justify-center p-8">
        <div className="bg-white/90 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <h1 className="text-4xl font-bold text-red-600 mb-4">💀 Game Over!</h1>
          <p className="text-gray-700 mb-4">
            You fell off the platforms!
          </p>
          <div className="mb-6">
            <p className="text-lg font-semibold text-gray-800">Final Score: {finalScore}</p>
            <p className="text-sm text-gray-600">Level: {finalLevel}</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => setGameState('menu')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2"
            >
              Try Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'menu') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-blue-400 to-blue-600 flex flex-col items-center justify-center p-8">
        <div className="bg-white/90 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <h1 className="text-3xl font-bold text-blue-600 mb-4">MapleOS Platformer</h1>
          <p className="text-gray-700 mb-4">
            A classic 2D side-scrolling adventure! Collect coins, avoid enemies, and reach the end of each level.
          </p>
          <p className="text-red-600 font-semibold mb-6">
            ⚠️ WARNING: There's no ground! Don't fall off the platforms or you'll die!
          </p>
          
          <div className="mb-6 text-left text-sm text-gray-600 space-y-2">
            <h3 className="font-semibold text-gray-800">Controls:</h3>
            <div className="flex items-center gap-2">
              <ArrowLeft size={16} />
              <ArrowRight size={16} />
              <span>Move left/right (or A/D)</span>
            </div>
            <div className="flex items-center gap-2">
              <Space size={16} />
              <span>Jump (or W/Up Arrow)</span>
            </div>
            <p className="text-xs mt-2">Touch: Left side = move left, Right side = move right, Center = jump</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => setGameState('playing')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2"
            >
              Start Game
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-background flex flex-col items-center justify-center p-4">
      <div className="mb-4 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleSound}
          className="flex items-center gap-2"
        >
          {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          {soundEnabled ? 'Sound On' : 'Sound Off'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={resetGame}
          className="flex items-center gap-2"
        >
          <RotateCcw size={16} />
          Reset
        </Button>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      
      <div className="border-4 border-gray-800 rounded-lg overflow-hidden shadow-2xl">
        <GameEngine
          key={gameKey}
          systems={[MovementSystem, CollisionSystem, EnemySystem, MovingPlatformSystem, CameraSystem]}
          entities={initialEntities}
          renderer={GameRenderer}
          running={gameState === 'playing'}
          onEvent={(entities) => {
            // Store game state reference continuously
            gameStateRef.current = entities;
          }}
          style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
        />
      </div>
      
      <div className="mt-4 text-center text-sm text-muted-foreground max-w-2xl">
        <p>Collect golden coins for points! Jump on enemies to defeat them. Complete levels to progress through increasingly challenging worlds.</p>
      </div>
    </div>
  );
};
